import React from "react";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { useSEO } from "../lib/useSEO";
import { Container, SectionHeading, CTAButton, Eyebrow, Reveal } from "../components/site/ui";
import { SupplyChainNetwork } from "../components/site/SupplyChainNetwork";
import { CycleDiagram } from "../components/site/CycleDiagram";
import { IntegrationLayer } from "../components/site/IntegrationLayer";

const VIDEO_URL = "/dynamis-video.m4v";
const POSTER = "https://images.unsplash.com/photo-1701313056413-0915e1adf204?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200";

export default function Home() {
  const { t, lang } = useLang();
  const h = t.home;
  useSEO({ title: h.metaTitle, description: h.metaDesc, path: "/", lang });

  return (
    <div>
      {/* HERO */}
      <section className="relative pt-28 pb-20 lg:pt-36 lg:pb-28 border-b border-cool-dark overflow-hidden" data-testid="hero-section">
        <Container className="grid lg:grid-cols-[1.05fr_1fr] gap-14 items-center">
          <div>
            <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <Eyebrow testId="hero-eyebrow">{h.eyebrow}</Eyebrow>
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 }}
              className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-white leading-[1.05]"
              data-testid="hero-title"
            >
              {h.title}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}
              className="mt-6 text-lg text-cool-light leading-relaxed max-w-xl" data-testid="hero-subtitle"
            >
              {h.subtitle}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.25 }}
              className="mt-9 flex flex-wrap gap-3"
            >
              <CTAButton to="/pilot-program" variant="primary" testId="hero-cta-primary">{t.common.applyPilot}</CTAButton>
              <CTAButton to="/platform" variant="secondary" testId="hero-cta-secondary">{t.common.discoverPlatform}</CTAButton>
            </motion.div>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.2 }}
            className="grid-borders bg-nb-deep p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs font-mono text-cool-mid">SUPPLY CHAIN · LIVE VIEW</span>
              <span className="w-2 h-2 rounded-full bg-cyan pulse-node" />
            </div>
            <SupplyChainNetwork />
          </motion.div>
        </Container>
      </section>

      {/* PROBLEM */}
      <section className="py-24 border-b border-cool-dark" data-testid="problem-section">
        <Container>
          <SectionHeading title={h.problemTitle} intro={h.problemBody} />
          <div className="mt-12 grid gap-px bg-cool-dark border border-cool-dark md:grid-cols-3">
            {h.problems.map((p, i) => (
              <Reveal key={i} delay={i * 0.1} className="bg-nb-base p-7">
                <span className="font-mono text-xs text-cyan">0{i + 1}</span>
                <h3 className="mt-3 font-display text-lg text-white">{p.t}</h3>
                <p className="mt-2 text-sm text-cool-light leading-relaxed">{p.d}</p>
              </Reveal>
            ))}
          </div>
          <p className="mt-10 text-xl md:text-2xl font-display text-white max-w-3xl leading-snug border-l-2 border-gold pl-5">
            {h.problemClose}
          </p>
        </Container>
      </section>

      {/* CYCLE */}
      <section className="py-24 border-b border-cool-dark" data-testid="how-it-works-section">
        <Container>
          <SectionHeading eyebrow="Sense → Simulate → Solve → Execute" title={h.cycleTitle} intro={h.cycleIntro} />
          <div className="mt-12"><CycleDiagram /></div>
        </Container>
      </section>

      {/* FEATURES — editorial */}
      <section className="py-24 border-b border-cool-dark" data-testid="features-section">
        <Container className="grid lg:grid-cols-[0.9fr_1.1fr] gap-14">
          <div className="lg:sticky lg:top-24 self-start">
            <SectionHeading title={h.featuresTitle} intro={h.featuresIntro} />
            <div className="mt-8"><SupplyChainNetwork /></div>
          </div>
          <div className="divide-y divide-cool-dark border-t border-cool-dark">
            {h.features.map((f, i) => (
              <Reveal key={i} delay={(i % 3) * 0.06}>
                <div className="py-5 flex gap-5 group">
                  <span className="font-mono text-xs text-cool-mid pt-1 w-8 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <h3 className="font-display text-lg text-white group-hover:text-cyan transition-colors duration-200">{f.t}</h3>
                    <p className="mt-1 text-sm text-cool-light leading-relaxed">{f.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* VALUE */}
      <section className="py-24 border-b border-cool-dark bg-nb-deep" data-testid="value-section">
        <Container>
          <SectionHeading title={h.valueTitle} intro={h.valueIntro} />
          <div className="mt-10 grid gap-4 sm:grid-cols-2">
            {h.values.map((v, i) => (
              <Reveal key={i} delay={(i % 2) * 0.06}>
                <div className="flex items-start gap-3 border border-cool-dark bg-nb-base p-5">
                  <CheckCircle2 size={18} className="text-cyan mt-0.5 shrink-0" strokeWidth={1.8} />
                  <span className="text-sm text-white leading-relaxed">{v}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* INTEGRATION */}
      <section className="py-24 border-b border-cool-dark" data-testid="integration-section">
        <Container>
          <SectionHeading eyebrow={t.nav.platform} title={h.integrationTitle} />
          <div className="mt-12"><IntegrationLayer /></div>
        </Container>
      </section>

      {/* VIDEO */}
      <section className="py-24 border-b border-cool-dark bg-nb-deep" data-testid="video-section">
        <Container>
          <SectionHeading title={h.videoTitle} intro={h.videoBody} />
          <Reveal className="mt-10">
            <div className="grid-borders bg-black overflow-hidden">
              <video
                data-testid="demo-video"
                controls
                preload="none"
                poster={POSTER}
                className="w-full h-auto block"
              >
                <source src={VIDEO_URL} type="video/mp4" />
              </video>
            </div>
          </Reveal>
          <div className="mt-6">
            <CTAButton
              to="https://investor.dynamisdecisionintelligence.com"
              external
              variant="secondary"
              testId="interactive-demo-cta"
            >
              {t.common.viewDemo}
            </CTAButton>
          </div>
        </Container>
      </section>

      {/* PILOT teaser */}
      <section className="py-24 border-b border-cool-dark" data-testid="pilot-teaser-section">
        <Container className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <Eyebrow>{t.nav.pilot}</Eyebrow>
            <h2 className="mt-3 text-3xl md:text-4xl font-display font-semibold text-white leading-tight">{h.pilotTitle}</h2>
            <p className="mt-4 text-cool-light leading-relaxed">{h.pilotIntro}</p>
            <div className="mt-8"><CTAButton to="/pilot-program" variant="gold" testId="pilot-teaser-cta">{t.common.applyPilot}</CTAButton></div>
          </div>
          <div className="space-y-px bg-cool-dark border border-cool-dark">
            {t.pilot.steps.map((s, i) => (
              <div key={i} className="bg-nb-base p-5 flex gap-4 items-start" data-testid={`pilot-teaser-step-${i}`}>
                <span className="font-mono text-cyan text-sm">0{i + 1}</span>
                <div>
                  <div className="font-display text-white">{s.t}</div>
                  <div className="text-sm text-cool-light">{s.d}</div>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* INDUSTRIES */}
      <section className="py-24 border-b border-cool-dark bg-nb-deep" data-testid="industries-section">
        <Container>
          <SectionHeading eyebrow={t.nav.industries} title={h.industriesTitle} intro={h.industriesIntro} />
          <div className="mt-10 grid gap-px bg-cool-dark border border-cool-dark sm:grid-cols-2 lg:grid-cols-3">
            {t.industries.items.map((ind, i) => (
              <Reveal key={i} delay={(i % 3) * 0.06} className="bg-nb-base p-6">
                <h3 className="font-display text-lg text-white">{ind.t}</h3>
                <p className="mt-2 text-sm text-cool-light leading-relaxed">{ind.d}</p>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* CLOSING */}
      <section className="py-28" data-testid="closing-section">
        <Container className="text-left max-w-4xl">
          <h2 className="text-3xl md:text-5xl font-display font-bold text-white leading-tight">{h.closingTitle}</h2>
          <p className="mt-6 text-lg text-cool-light leading-relaxed max-w-3xl">{h.closingBody}</p>
          <div className="mt-9 flex flex-wrap gap-3">
            <CTAButton to="/contact" variant="primary" testId="closing-cta">{t.common.talkUseCase}</CTAButton>
            <CTAButton to="/pilot-program" variant="secondary" testId="closing-cta-pilot">{t.common.applyPilot}</CTAButton>
          </div>
        </Container>
      </section>
    </div>
  );
}
